import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Upload, FileText, ScanLine, Check, Sparkles } from "lucide-react";

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastExtract, setLastExtract] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setLastExtract(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLastExtract({ doc: data.document, ex: data.extracted });
      toast.success("Document analysé par OMY");
      load();
    } catch (err) {
      toast.error("Échec de l'analyse du document");
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const createItem = async (docId) => {
    await api.post(`/documents/${docId}/create-item`);
    toast.success("Bien créé depuis le document");
    setLastExtract(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Coffre documentaire</h1>
        <p className="text-muted-foreground text-sm mt-1">Photographie un ticket, une facture, un contrat. OMY lit, extrait et classe (OCR + IA).</p>
      </div>

      <input ref={fileRef} type="file" data-testid="doc-upload-input" onChange={onFile} className="hidden"
        accept="image/*,application/pdf" />
      <button data-testid="doc-upload-btn" onClick={() => fileRef.current?.click()} disabled={busy}
        className="w-full omy-glass rounded-3xl p-10 border-dashed border-2 border-white/10 hover:border-primary/40 transition-all flex flex-col items-center gap-3">
        {busy ? <Sparkles className="text-primary animate-pulse" size={28} /> : <Upload className="text-primary" size={28} />}
        <span className="font-display font-medium">{busy ? "OMY analyse ton document…" : "Importer un document"}</span>
        <span className="text-xs text-muted-foreground">PDF, photo, ticket, facture — OMY conserve toujours l'original</span>
      </button>

      {lastExtract && (
        <div data-testid="doc-extract-result" className="omy-glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3"><ScanLine size={18} className="text-primary" />
            <h3 className="font-display font-medium">Données extraites par OMY</h3></div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {[["Type", lastExtract.ex?.doc_type], ["Commerçant", lastExtract.ex?.merchant],
              ["Date", lastExtract.ex?.date], ["Total", lastExtract.ex?.total != null ? `${lastExtract.ex.total} ${lastExtract.ex.currency || "EUR"}` : null],
              ["Produit", lastExtract.ex?.product_name], ["Marque", lastExtract.ex?.brand],
              ["N° série", lastExtract.ex?.serial], ["Garantie", lastExtract.ex?.warranty_months && `${lastExtract.ex.warranty_months} mois`]]
              .filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-white/5 py-1.5">
                  <span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span>
                </div>
              ))}
          </div>
          {lastExtract.ex?.summary && <p className="text-sm text-muted-foreground mt-3">{lastExtract.ex.summary}</p>}
          <Button data-testid="doc-create-item-btn" onClick={() => createItem(lastExtract.doc.id)}
            className="mt-4 bg-primary hover:bg-primary/90 rounded-full">
            <Check size={16} className="mr-1.5" /> Ajouter ce produit à mes biens
          </Button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {docs.map((d, i) => (
          <div key={d.id} data-testid={`doc-card-${i}`} className="omy-glass rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center"><FileText size={18} className="text-primary" /></div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{d.original_filename}</p>
                <p className="text-xs text-muted-foreground">{d.doc_type} · {fmtDate(d.created_at)}</p>
              </div>
            </div>
            {d.summary && <p className="text-xs text-muted-foreground mt-3 line-clamp-2">{d.summary}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
